import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, Upload, FileText, AlertCircle, CheckCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useUIStore } from '@/client/stores/ui-store';
import { apiFetch } from '@/client/lib/api';
import { useKeyboard } from '@/client/hooks/use-keyboard';

interface ImportResult {
  success: number;
  failed: number;
  errors: { row: number; url: string; error: string }[];
  links: unknown[];
}

export function ImportLinksModal() {
  const { importLinksModalOpen, closeImportLinksModal } = useUIStore();
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const importMutation = useMutation({
    mutationFn: async (csvFile: File) => {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await apiFetch('/api/links/import', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to import links');
      }
      return response.json() as Promise<ImportResult>;
    },
    onSuccess: (data) => {
      setResult(data);
      if (data.success > 0) {
        queryClient.invalidateQueries({ queryKey: ['links'] });
        toast.success(
          `Successfully imported ${data.success} link${data.success !== 1 ? 's' : ''}!`
        );
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile?.name.endsWith('.csv')) {
      setFile(droppedFile);
      setResult(null);
    } else {
      toast.error('Please drop a CSV file');
    }
  };

  const handleSubmit = () => {
    if (file) {
      importMutation.mutate(file);
    }
  };

  const handleClose = useCallback(() => {
    closeImportLinksModal();
    setFile(null);
    setResult(null);
  }, [closeImportLinksModal]);

  const downloadTemplate = () => {
    const template =
      'url,title,tags,vanity_name\nhttps://example.com,My Example Link,marketing;promo,\nhttps://google.com,Google,,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'links-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!importLinksModalOpen) {
      setFile(null);
      setResult(null);
    }
  }, [importLinksModalOpen]);

  // ⚡ Performance Optimization: Use shared keyboard hook instead of manual listener
  useKeyboard('Escape', handleClose, importLinksModalOpen);

  if (!importLinksModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg mx-4 bg-background rounded-lg shadow-xl border animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Import Links</h2>
          <button
            onClick={handleClose}
            aria-label="Close import links modal"
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Template download */}
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
          >
            <Download className="h-4 w-4" />
            Download CSV template
          </button>

          {/* File drop zone */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver
                ? 'border-primary bg-primary/5'
                : file
                  ? 'border-green-500/50 bg-green-500/5'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />

            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileText className="h-12 w-12 text-green-500" />
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">Click to change file</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-12 w-12 text-muted-foreground" />
                <p className="font-medium">Drop your CSV file here</p>
                <p className="text-sm text-muted-foreground">or click to browse</p>
              </div>
            )}
          </div>

          {/* CSV format info */}
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">
            <p className="font-medium mb-2">CSV Format</p>
            <ul className="space-y-1 text-xs">
              <li>
                <code className="bg-muted px-1 rounded">url</code> - Destination URL (required)
              </li>
              <li>
                <code className="bg-muted px-1 rounded">title</code> - Link title (optional)
              </li>
              <li>
                <code className="bg-muted px-1 rounded">tags</code> - Semicolon-separated tags
                (optional)
              </li>
              <li>
                <code className="bg-muted px-1 rounded">vanity_name</code> - Vanity URL name (Plus
                only)
              </li>
            </ul>
            <p className="mt-2 text-xs">Maximum 500 rows per import</p>
          </div>

          {/* Import results */}
          {result && (
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                {result.success > 0 && (
                  <div className="flex items-center gap-2 text-green-500">
                    <CheckCircle className="h-5 w-5" />
                    <span>{result.success} imported</span>
                  </div>
                )}
                {result.failed > 0 && (
                  <div className="flex items-center gap-2 text-red-500">
                    <AlertCircle className="h-5 w-5" />
                    <span>{result.failed} failed</span>
                  </div>
                )}
              </div>

              {result.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-lg border bg-muted/30 p-3">
                  <p className="text-sm font-medium mb-2">Errors:</p>
                  <ul className="space-y-1 text-xs">
                    {result.errors.slice(0, 10).map((err, i) => (
                      <li key={i} className="text-red-500">
                        Row {err.row}: {err.error}
                        {err.url && <span className="text-muted-foreground"> ({err.url})</span>}
                      </li>
                    ))}
                    {result.errors.length > 10 && (
                      <li className="text-muted-foreground">
                        ... and {result.errors.length - 10} more errors
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 rounded-md border hover:bg-muted transition-colors"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!file || importMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Import Links
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
